/**
 * One card per customer: tractors production ready now against the
 * threshold a fleet admin set, runs over the span, and a bar per day of
 * tractors ready at the end of that day. Counts, not hours: a tractor can
 * change customer within a span, so hour sums would get noisy.
 */
import React from "react";
import { Link } from "react-router-dom";
import { styled } from "styled-components";
import { customerLabel, dayHeader } from "./labels";
import { FleetCustomer } from "./types";
import { tokens } from "../TractorState/tokens";

interface CustomerCardsProps {
  customers: FleetCustomer[];
}

function status(c: FleetCustomer): {
  tone: "ok" | "bad" | "muted";
  text: string;
} {
  if (c.threshold === null) return { tone: "muted", text: "No threshold set" };
  if (c.ready_now < c.threshold) {
    return {
      tone: "bad",
      text: `Below threshold now · ${c.ready_now} ready, threshold ${c.threshold}`,
    };
  }
  return {
    tone: "ok",
    text: `Above threshold · ${c.ready_now} ready, threshold ${c.threshold}`,
  };
}

const CustomerCards: React.FC<CustomerCardsProps> = ({ customers }) => {
  if (customers.length === 0) return null;
  return (
    <Grid $count={customers.length}>
      {customers.map((c) => {
        const label = customerLabel(c.customer);
        const s = status(c);
        return (
          <Card key={c.customer} aria-label={label}>
            <Head>
              <Title>
                {label} · {c.tractors} tractor{c.tractors === 1 ? "" : "s"}
              </Title>
              <Status $tone={s.tone}>{s.text}</Status>
            </Head>
            <Numbers>
              <span>
                <Key>Ready now </Key>
                <Value $tone={s.tone === "muted" ? undefined : s.tone}>
                  {c.ready_now} of {c.tractors}
                </Value>
              </span>
              <span>
                <Key>Threshold </Key>
                <Value>{c.threshold === null ? "none" : c.threshold}</Value>
              </span>
              <span>
                <Key>Runs </Key>
                <Value>{c.runs}</Value>
              </span>
            </Numbers>
            <Bars
              role="img"
              aria-label={`${label}: tractors production ready at the end of each day${
                c.threshold === null ? "" : `, threshold ${c.threshold}`
              }`}
            >
              {c.threshold !== null && c.tractors > 0 && (
                <ThresholdLine
                  style={{
                    bottom: `${Math.min(100, (c.threshold / c.tractors) * 100)}%`,
                  }}
                />
              )}
              {c.days.map((d, i) => {
                const share = c.tractors ? d.ready_end_of_day / c.tractors : 0;
                const below =
                  c.threshold !== null && d.ready_end_of_day < c.threshold;
                const last = i === c.days.length - 1;
                return (
                  <Bar
                    key={d.local_day}
                    $below={below}
                    $faded={last}
                    style={{ height: `${Math.max(4, share * 100)}%` }}
                    title={`${dayHeader(d.local_day)}: ${d.ready_end_of_day} of ${c.tractors}`}
                  />
                );
              })}
            </Bars>
            <Foot>
              <span>
                Bars = tractors production ready at end of day
                {c.threshold !== null
                  ? " · dashed line = threshold · red = below it"
                  : ""}
                {c.days.length ? " · last bar is now" : ""}
              </span>
              <Link to="/tractor-state/schema">Edit thresholds</Link>
            </Foot>
          </Card>
        );
      })}
    </Grid>
  );
};

export default CustomerCards;

const Grid = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: repeat(
    ${(p) => Math.min(p.$count, 2)},
    minmax(0, 1fr)
  );
  gap: 16px;
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: ${tokens.white};
  border: 1px solid ${tokens.line};
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: baseline;
  gap: 4px 10px;
`;

const Title = styled.h2`
  margin: 0;
  font: 600 14px ${tokens.fontSans};
  color: ${tokens.ink};
`;

const Status = styled.span<{ $tone: "ok" | "bad" | "muted" }>`
  font: 600 12px ${tokens.fontSans};
  color: ${(p) =>
    p.$tone === "ok"
      ? tokens.readyText
      : p.$tone === "bad"
        ? tokens.groundedText
        : tokens.slate};
`;

const Numbers = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 22px;
  align-items: baseline;
`;

const Key = styled.span`
  font: 11px ${tokens.fontMono};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${tokens.slate};
`;

const Value = styled.span<{ $tone?: "ok" | "bad" }>`
  font: 600 14px ${tokens.fontSans};
  font-variant-numeric: tabular-nums;
  color: ${(p) =>
    p.$tone === "ok"
      ? tokens.readyText
      : p.$tone === "bad"
        ? tokens.groundedText
        : tokens.ink};
`;

const Bars = styled.div`
  position: relative;
  display: flex;
  gap: 4px;
  align-items: flex-end;
  height: 34px;
`;

const ThresholdLine = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  border-top: 1px dashed ${tokens.slate};
  pointer-events: none;
`;

const Bar = styled.span<{ $below: boolean; $faded: boolean }>`
  flex: 1;
  border-radius: 2px;
  background: ${(p) => (p.$below ? "#c6262b" : "#8fd79f")};
  opacity: ${(p) => (p.$faded ? 0.6 : 1)};
`;

const Foot = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px 12px;
  font: 12px ${tokens.fontSans};
  color: ${tokens.slate};
  a {
    font-weight: 600;
    color: ${tokens.blue};
    text-decoration: none;
    white-space: nowrap;
  }
`;
